import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const offerBuildArticle: BlogArticle = {
  key: BlogArticleKey.OFFER_BUILD,
  slug: BlogSlugs.OFFER_BUILD,
  categoryId: "01",
  date: "2026-07-22",
  readTime: 6,
  locales: {
    fr: {
      title:
        "Comment construire une offre qui vend, à partir de ce que disent vraiment tes clients",
      excerpt:
        "La plupart des offres sont écrites en réunion, à partir d'une intuition. C'est pour ça qu'elles sonnent creux. Une offre qui vend part d'ailleurs.",
      description:
        "Une offre qui vend ne part pas d'une intuition, mais de ce que disent vraiment tes clients. La méthode en 4 étapes pour une offre claire et désirable.",
      ctaTitle: "Construis ton offre sur la vraie voix de tes clients",
      ctaText:
        "On part de ce que tes clients disent déjà pour en tirer la promesse, le problème et les mots de ton offre. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tu as un bon produit, ou une vraie expertise. Pourtant, quand tu présentes ton offre, les prospects hésitent, comparent, ou repartent en disant qu'ils vont réfléchir. Le problème n'est presque jamais le produit. C'est l'offre : la façon dont tu formules ce que tu proposes, et pour qui.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La bonne nouvelle, c'est qu'une offre qui vend ne se devine pas. Elle se construit, méthodiquement, à partir d'une matière que tu as déjà : ce que tes clients te disent.",
        },
        { type: ArticleBlockType.H2, text: "Pourquoi la plupart des offres tombent à plat" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La majorité des offres souffrent du même défaut : elles partent du produit, pas du client. On liste des fonctionnalités, des livrables, des heures d'accompagnement. On décrit ce qu'on fait, pas ce que ça change pour la personne en face.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Résultat, une offre qui parle de toi et pas de ton client :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Elle décrit des caractéristiques (**un accompagnement de 3 mois, 6 sessions**) au lieu d'un résultat (**signer tes 3 premiers clients**).",
            "Elle emploie ton vocabulaire d'expert, pas les mots que ton client utilise pour décrire son problème.",
            "Elle vise tout le monde, donc ne résonne avec personne en particulier.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Une offre ne devient désirable que quand le client s'y reconnaît. Or on ne peut pas se reconnaître dans un discours qui parle du vendeur.",
        },
        { type: ArticleBlockType.H2, text: "Le vrai point de départ : la voix de tes clients" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une offre qui vend se construit à l'envers de ce qu'on croit. On ne part pas de *qu'est-ce que je propose*, mais de *qu'est-ce que mes clients veulent vraiment résoudre, et comment le disent-ils*.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cette matière, tu l'as déjà. Elle est dans tes échanges : tes appels de vente, les retours de tes clients, les questions qui reviennent, les objections que tu entends. C'est là que se cachent les vraies douleurs et, surtout, **les mots exacts que tes clients emploient**. Ces mots valent de l'or, parce qu'une offre formulée avec les mots du client se vend seule.",
        },
        { type: ArticleBlockType.H2, text: "Construire son offre en 4 étapes" },
        { type: ArticleBlockType.H3, text: "1. Collecte la matière brute" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Reprends tes derniers appels, tes retours clients, tes échanges. Note les phrases exactes : comment ils décrivent leur problème, ce qu'ils ont essayé avant, ce qui les bloque. Ne reformule pas encore, collecte les mots bruts.",
        },
        { type: ArticleBlockType.H3, text: "2. Identifie la douleur numéro un" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Dans cette matière, une douleur revient plus que les autres. Pas celle que tu crois la plus importante : celle qu'ils citent le plus souvent, avec le plus d'émotion. C'est le cœur de ton offre.",
        },
        { type: ArticleBlockType.H3, text: "3. Formule la promesse avec leurs mots" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Transforme cette douleur en résultat désirable, en reprenant leur vocabulaire. Si tes clients disent *je perds un temps fou à trier*, ta promesse parle de temps gagné, pas de process optimisé. Le client doit se dire : c'est exactement mon problème.",
        },
        { type: ArticleBlockType.H3, text: "4. Structure et teste" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Assemble ton offre autour de cette promesse : à qui elle s'adresse, ce qu'elle résout, ce qu'elle inclut. Puis teste-la sur de vrais prospects et écoute leur réaction. S'ils la reformulent spontanément avec tes mots, tu as gagné. Sinon, retourne à l'étape 1.",
        },
        { type: ArticleBlockType.H2, text: "Le test qui ne trompe pas" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une offre est claire et juste quand un prospect peut la répéter avec ses propres mots après l'avoir entendue une seule fois. S'il faut réexpliquer, reformuler, préciser, c'est que l'offre n'est pas encore alignée sur sa façon de penser son problème.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est la différence entre une offre qu'on subit et une offre qui se transmet. La première, tu la défends. La seconde, tes clients la racontent à ta place.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Construire une offre sur la voix de tes clients suppose de rassembler cette voix quelque part. On analyse tes appels et tes échanges, on en extrait les douleurs, les objections et les mots récurrents, et on t'aide à en tirer une offre alignée sur ce qu'ils disent vraiment. Une donnée captée une fois qui sert à comprendre, positionner, puis vendre.",
          linkLabel: "Découvrir l'analyse des appels",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une offre qui vend n'est pas une question de talent d'écriture ou d'inspiration. C'est une question de matière. Pars de ce que disent vraiment tes clients, isole leur douleur principale, formule ta promesse avec leurs mots, et teste. Tu passeras d'une offre que tu défends à une offre qui se vend d'elle-même.",
        },
      ],
      faq: [
        {
          question: "Comment savoir si mon offre est claire ?",
          answer:
            "Une offre est claire quand un prospect peut la reformuler avec ses propres mots après l'avoir entendue une fois. Si tu dois réexpliquer, elle ne l'est pas encore. Le meilleur test : écoute comment tes clients décrivent ce que tu fais, et reprends leurs mots.",
        },
        {
          question: "Faut-il partir de son produit ou de son client pour construire une offre ?",
          answer:
            "Du client. Une offre qui part du produit décrit des fonctionnalités. Une offre qui part du client répond à une douleur réelle, avec les mots du client. C'est ce qui la rend désirable.",
        },
        {
          question: "Où trouver la matière pour construire une bonne offre ?",
          answer:
            "Dans tes échanges clients : appels de vente, retours, objections, questions récurrentes. C'est là que se trouvent les vraies douleurs et les mots exacts que tes clients emploient.",
        },
      ],
    },
    en: {
      title: "Build an offer that sells from what your customers say",
      excerpt:
        "Most offers are written in a meeting, from a hunch. That is why they ring hollow. An offer that sells starts somewhere else.",
      description:
        "An offer that sells does not start from a hunch, but from what your customers actually say. The four-step method for a clear, desirable offer.",
      ctaTitle: "Build your offer on what your customers actually say",
      ctaText:
        "We start from what your customers already say and pull out the promise, the problem and the words of your offer. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You have a good product, or real expertise. Yet when you present your offer, prospects hesitate, compare, or leave saying they will think about it. The problem is almost never the product. It is the offer: how you word what you are selling, and who for.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The good news is that an offer that sells is not guesswork. It is built, methodically, from material you already have: what your customers tell you.",
        },
        { type: ArticleBlockType.H2, text: "Why most offers fall flat" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Most offers share the same flaw: they start from the product, not the customer. They list features, deliverables, hours of support. They describe what you do, not what it changes for the person in front of you.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The result is an offer that talks about you rather than your customer:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "It describes characteristics (**three months of support, six sessions**) instead of an outcome (**signing your first three clients**).",
            "It uses your expert vocabulary, not the words your customer uses to describe their problem.",
            "It targets everyone, so it resonates with no one in particular.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "An offer only becomes desirable when the customer recognises themselves in it. And nobody recognises themselves in a pitch about the seller.",
        },
        { type: ArticleBlockType.H2, text: "The real starting point: your customers' words" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An offer that sells is built the other way round from what most people assume. You do not start from *what am I selling*, but from *what do my customers actually want to solve, and how do they say it*.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You already have that material. It sits in your conversations: sales calls, customer feedback, the questions that keep coming back, the objections you hear. That is where the real pains hide and, above all, **the exact words your customers use**. Those words are worth gold, because an offer written in the customer's words sells itself.",
        },
        { type: ArticleBlockType.H2, text: "Building your offer in four steps" },
        { type: ArticleBlockType.H3, text: "1. Collect the raw material" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Go back through your last calls, your customer feedback, your exchanges. Write down the exact sentences: how they describe their problem, what they tried before, what blocks them. Do not rephrase yet, collect the raw words.",
        },
        { type: ArticleBlockType.H3, text: "2. Find the number one pain" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In that material, one pain comes back more than the others. Not the one you believe matters most: the one they mention most often, with the most emotion. That is the heart of your offer.",
        },
        { type: ArticleBlockType.H3, text: "3. Word the promise with their words" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Turn that pain into a desirable outcome, using their vocabulary. If your customers say *I waste so much time sorting through it*, your promise talks about time gained, not about an optimised process. The customer has to think: that is exactly my problem.",
        },
        { type: ArticleBlockType.H3, text: "4. Structure it and test it" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Assemble your offer around that promise: who it is for, what it solves, what it includes. Then test it on real prospects and listen to their reaction. If they spontaneously repeat it back in your words, you have it. If not, go back to step one.",
        },
        { type: ArticleBlockType.H2, text: "The test that never lies" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An offer is clear and right when a prospect can repeat it in their own words after hearing it once. If it takes re-explaining, rephrasing, clarifying, then the offer is not yet aligned with how they think about their problem.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That is the difference between an offer you have to defend and an offer that travels. The first one you argue for. The second one your customers tell on your behalf.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "Building an offer on your customers' words means gathering those words somewhere. We analyse your calls and your conversations, pull out the pains, the objections and the recurring words, and help you turn them into an offer aligned with what they actually say. Data captured once, used to understand, position, then sell.",
          linkLabel: "See how call analysis works",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An offer that sells is not a matter of writing talent or inspiration. It is a matter of material. Start from what your customers actually say, isolate their main pain, word your promise with their words, and test. You will move from an offer you defend to an offer that sells itself.",
        },
      ],
      faq: [
        {
          question: "How do I know if my offer is clear?",
          answer:
            "An offer is clear when a prospect can repeat it in their own words after hearing it once. If you have to re-explain, it is not there yet. The best test: listen to how your customers describe what you do, and use their words.",
        },
        {
          question: "Should an offer start from the product or from the customer?",
          answer:
            "From the customer. An offer that starts from the product describes features. An offer that starts from the customer answers a real pain, in the customer's words. That is what makes it desirable.",
        },
        {
          question: "Where do I find the material to build a good offer?",
          answer:
            "In your customer conversations: sales calls, feedback, objections, recurring questions. That is where the real pains sit, along with the exact words your customers use.",
        },
      ],
    },
  },
};
